import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AzTagComponent } from '../az-tag/az-tag.component';

type ModalState =
  | 'confirm-change'
  | 'review'
  | 'confirm-reject'
  | 'success'
  | 'success-with-message'
  | 'rejected'
  | 'already-processed'
  | 'request-sent'
  | 'processing'
  | 'error'
  | 'custom-confirm';

@Component({
  selector: 'app-status-change-modal',
  standalone: true,
  imports: [CommonModule, AzTagComponent],
  templateUrl: './status-change-modal.component.html',
  styleUrl: './status-change-modal.component.scss',
})
export class StatusChangeModalComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() title = 'Status Change'; // Default title, can be overridden
  @Input() legalEntityName = '';
  @Input() currentStatus = '';
  @Input() newStatus = '';
  @Input() alreadyProcessedBy = '';
  @Input() successMessage = ''; // Dynamic message from backend response
  @Input() errorMessage = ''; // Dynamic error message
  @Input() warningMessage = ''; // Warning message to show above success content
  @Input() warningLinkText = ''; // Text for the link in warning message
  @Input() warningLinkUrl = ''; // URL for the link in warning message
  @Input() errorDetails: Array<{ message: string }> = []; // Detailed error messages for bullet list
  @Input() resultTitle = ''; // Dynamic title for result states (error, success, etc.)
  @Input() processingResultTitle = 'Loading...'; // Dynamic subtitle shown inside the processing state
  @Input() resultVariant: 'success' | 'error' | 'rejected' | 'default' = 'default'; // Variant for result icon styling
  @Input() initialState: ModalState = 'confirm-change'; // Default to confirm-change, can be overridden

  // Custom confirm modal inputs
  @Input() customMessage = '';
  @Input() customNoButtonLabel = 'No';
  @Input() customYesButtonLabel = 'Yes';
  @Input() hideButtons = false; // Hide buttons in custom-confirm variant
  // When true, clicking the backdrop does not close the modal — only explicit
  // actions (close button, No/Yes buttons) do. Defaults to false to preserve
  // existing behavior for all current usages of this shared component.
  @Input() disableBackdropClose = false;

  @Output() approve = new EventEmitter<void>();
  @Output() reject = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();
  @Output() confirmChange = new EventEmitter<void>();
  @Output() customNo = new EventEmitter<void>();
  @Output() customYes = new EventEmitter<void>();

  // Internal state management
  state: ModalState = 'confirm-change';
  // Stores the last title value explicitly provided via @Input so resets don't hardcode 'Status Change'
  private _titleFromInput = 'Status Change';

  ngOnChanges(changes: SimpleChanges) {
    // Track the title provided via @Input binding
    if (changes['title']) {
      this._titleFromInput = changes['title'].currentValue;
    }

    // Use the initialState input when modal opens, but don't override if already showing a result state
    if (this.isOpen && changes['isOpen']) {
      // Only set initialState if not already in a result state set by method calls
      const resultStates: ModalState[] = [
        'error',
        'success-with-message',
        'success',
        'rejected',
        'request-sent',
      ];
      if (!resultStates.includes(this.state)) {
        this.state = this.initialState;
        // Reset title to the value provided via @Input (respects explicit [title] bindings)
        if (this.initialState !== 'custom-confirm') {
          this.title = this._titleFromInput;
        }
      }
    }

    // If alreadyProcessedBy changes and has a value, show already-processed state
    if (changes['alreadyProcessedBy'] && this.alreadyProcessedBy && this.isOpen) {
      this.state = 'already-processed';
    }
  }

  // State: confirm-change -> User clicks "Request Change"
  onRequestChange() {
    // Show processing state while API call is in progress
    this.title = 'Processing Status Change';
    this.state = 'processing';
    // Emit after state change to allow UI to update
    setTimeout(() => this.confirmChange.emit(), 0);
  }

  // State: confirm-change -> User clicks "Cancel"
  onCancelChange() {
    this.onClose();
  }

  // State: review -> User clicks "Approve"
  onApprove() {
    // Show processing state while API call is in progress
    this.title = 'Processing Status Change';
    this.state = 'processing';
    // Emit after state change to allow UI to update
    setTimeout(() => this.approve.emit(), 0);
  }

  // State: review -> User clicks "Reject" -> show confirmation
  onReject() {
    this.state = 'confirm-reject';
  }

  // State: confirm-reject -> User clicks "Yes" (confirm rejection)
  onConfirmReject() {
    // Show processing state while API call is in progress
    this.title = 'Processing Status Change';
    this.state = 'processing';
    // Emit after state change to allow UI to update
    setTimeout(() => this.reject.emit(), 0);
  }

  // State: confirm-reject -> User clicks "No" (cancel rejection)
  onCancelReject() {
    this.state = 'review';
  }

  onClose() {
    this.state = 'review'; // Reset state on close
    this.title = this._titleFromInput; // Reset title to the @Input-provided value
    this.close.emit();
  }

  // Show already processed state (called from parent when API returns error)
  showAlreadyProcessed() {
    this.title = 'Status Change';
    this.state = 'already-processed';
  }

  // Show success state (called from parent after successful approval)
  showSuccess() {
    this.title = 'Status Change';
    this.state = 'success';
    // Auto-close after 2 seconds
    setTimeout(() => {
      this.onClose();
    }, 2000);
  }

  // Show rejected state (called from parent after successful rejection)
  showRejected() {
    this.title = 'Status Change';
    this.state = 'rejected';
    // Auto-close after 2 seconds
    setTimeout(() => {
      this.onClose();
    }, 2000);
  }

  // Show request-sent state (called from parent after successful request submission)
  showRequestSent() {
    this.title = 'Status Change';
    this.state = 'request-sent';
    // Auto-close after 2 seconds
    setTimeout(() => {
      this.onClose();
    }, 2000);
  }

  // Show success with custom message from backend (called from parent with response message)
  showSuccessWithMessage(message: string, modalTitle?: string) {
    this.title = modalTitle || 'Status Change';
    this.successMessage = message;
    this.state = 'success-with-message';
  }

  // Show error with custom message (called from parent when upload/validation fails)
  showError(
    message: string,
    resultTitle?: string,
    variant?: string,
    errorDetails?: Array<{ message: string }>,
    modalTitle?: string,
  ) {
    this.errorMessage = message;
    this.resultTitle = resultTitle || 'Error';
    this.resultVariant = (variant as 'error' | 'success' | 'rejected' | 'default') || 'error';
    this.errorDetails = errorDetails || [];
    this.title = modalTitle || 'Warning';
    this.state = 'error';
  }

  // Show custom confirm state (called from parent with custom text)
  showCustomConfirm(title: string, message: string, noLabel: string, yesLabel: string) {
    this.title = title;
    this.customMessage = message;
    this.customNoButtonLabel = noLabel;
    this.customYesButtonLabel = yesLabel;
    this.state = 'custom-confirm';
  }

  // State: custom-confirm -> User clicks custom No button
  onCustomNo() {
    this.customNo.emit();
  }

  // State: custom-confirm -> User clicks custom Yes button
  onCustomYes() {
    this.customYes.emit();
  }

  onBackdropClick(event: MouseEvent) {
    if (this.disableBackdropClose) {
      return;
    }
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  // Helper methods to split status
  getCurrentStatusPrimary(): string {
    return this.getStatusPrimary(this.currentStatus);
  }

  getCurrentStatusSecondary(): string {
    return this.getStatusSecondary(this.currentStatus);
  }

  getNewStatusPrimary(): string {
    return this.getStatusPrimary(this.newStatus);
  }

  getNewStatusSecondary(): string {
    return this.getStatusSecondary(this.newStatus);
  }

  private getStatusPrimary(status: string): string {
    if (!status) return '';
    const idx = status.indexOf(':');
    if (idx === -1) return status.trim();
    return status.slice(0, idx).trim();
  }

  private getStatusSecondary(status: string): string {
    if (!status) return '';
    const idx = status.indexOf(':');
    if (idx === -1) return '';
    return status.slice(idx + 1).trim();
  }
}
